import { useState } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { Search, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useOrders } from '../../hooks/useOrders';
import { useCategories } from '../../hooks/useCategories';

interface MenusPageProps {
  onNavigate?: (page: string) => void;
  onCategoryClick?: (categoryId: string) => void;
  menuTypeId?: string;
}

export function MenusPage({ onNavigate, onCategoryClick, menuTypeId }: MenusPageProps) {
  // Update document title dynamically
  usePageTitle('Menus');

  // Default to closed on mobile, open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return true;
  });
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch data from API
  const { orders:ordersData, stats} = useOrders({ page: 1, limit: 50 });

  const { categories: apiCategories, loading: categoriesLoading, error: categoriesError, refresh } = useCategories(menuTypeId);

  // Map API categories to display format
  const categories = apiCategories.map(category => {
    const itemCount = category._count?.products ?? 0;
    return {
      id: category.id,
      name: category.name,
      description: category.description || 'Browse our selection',
      icon: getCategoryIcon(category.name),
      active: category.isActive,
      itemCount,
      image: category.image, 
    };
  });

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCategoryClick = (categoryId: string) => {
    if (onCategoryClick) {
      onCategoryClick(categoryId);
    } else {
      onNavigate?.(`menu-items-${categoryId}`); 
    }
  };

  // Helper function to get icon based on category name
  function getCategoryIcon(name: string): string {
    const iconMap: Record<string, string> = {
      'Appetizers': '🥗',
      'Main Courses': '🍽️',
      'Desserts': '🍰',
      'Beverages': '🥤',
      'Salads': '🥗',
      'Burgers': '🍔',
      'Seafood': '🦞',
      'Steaks': '🥩',
    };
    return iconMap[name] || '🍴';
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="menus"
        onNavigate={onNavigate}
        ordersCount={stats?.total || ordersData.length}
      />
      
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
      }`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Page Header */}
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Menu Categories</h1>
                <p className="text-gray-400 dark:text-gray-500 mt-1 text-sm">Manage your restaurant menu categories and items</p>
              </div>
            </div>

            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Categories Grid */}
            {categoriesLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-3" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading categories…</p>
                </div>
              </div>
            ) : categoriesError ? (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-8 text-center">
                <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                <p className="text-sm font-semibold text-red-700 dark:text-red-400 mb-4">{categoriesError}</p>
                <button
                  onClick={refresh}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-semibold mx-auto"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Try Again
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryClick(category.id)}
                    className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden hover:shadow-lg hover:scale-105 transition-all duration-200 text-left group"
                  >
                    {/* Category Image */}
                    {category.image ? (
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={category.image}
                          alt={category.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                          <span className="text-3xl">{category.icon}</span>
                          <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                            category.active 
                              ? 'bg-green-500 text-white' 
                              : 'bg-gray-700 text-gray-300'
                          }`}>
                            {category.active ? 'Active' : 'Inactive'}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-16 h-16 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-2xl flex items-center justify-center text-3xl group-hover:scale-110 transition-transform">
                            {category.icon}
                          </div>
                          <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                            category.active 
                              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                          }`}>
                            {category.active ? 'Active' : 'Inactive'}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="p-4">
                      {/* Category Info */}
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                        {category.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">
                        {category.description}
                      </p>

                      {/* Item Count */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                            <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{category.itemCount}</span>
                          </div>
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {category.itemCount === 1 ? 'item' : 'items'}
                          </span>
                        </div>
                        <div className="text-indigo-600 dark:text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Empty State */}
            {filteredCategories.length === 0 && !categoriesLoading && !categoriesError && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No categories found</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Try adjusting your search query
                </p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
